import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Field } from '@/shared/ui';

import { useLogin } from '../queries/useSession';
import { AuthLayout } from './AuthLayout';
import styles from './AuthLayout.module.css';

interface LocationState {
  from?: string;
}

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const describeError = useApiErrorMessage();
  const loginMutation = useLogin();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);

  // Spec 4A: cała platforma jest sterowana URL-em, więc po zalogowaniu wracamy
  // DOKŁADNIE tam, skąd przyszedł. Bez tego link do elementu wklejony w Jirę
  // wyrzuca zalogowanego na pulpit i każe szukać ręcznie.
  const returnTo = (location.state as LocationState | null)?.from ?? '/';

  const loginError = touched && login.trim() === '' ? t('auth.validation.loginRequired') : undefined;
  const passwordError =
    touched && password === '' ? t('auth.validation.passwordRequired') : undefined;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);

    if (login.trim() === '' || password === '') return;

    loginMutation.mutate(
      { login: login.trim(), password },
      { onSuccess: () => void navigate(returnTo, { replace: true }) },
    );
  }

  return (
    <AuthLayout title={t('auth.login.title')} subtitle={t('auth.login.subtitle')}>
      <form className={styles['form']} onSubmit={onSubmit} noValidate>
        {loginMutation.isError ? (
          <div className={styles['formError']} role="alert">
            {describeError(loginMutation.error)}
          </div>
        ) : null}

        <Field
          label={t('auth.login.loginField')}
          type="email"
          value={login}
          autoComplete="username"
          onChange={(event) => setLogin(event.target.value)}
          error={loginError}
        />

        <Field
          label={t('auth.login.passwordField')}
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.target.value)}
          error={passwordError}
        />

        <Button type="submit" variant="primary" block disabled={loginMutation.isPending}>
          {loginMutation.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
